import React from 'react';

import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { LinkedZoneDoc } from '@ledium/types';
import { LinkTypeChip } from '@ledium/ui-common';
import { Box, Button, Chip, Skeleton, Typography } from '@mui/material';

const chipSx = { height: 20, fontSize: '0.65rem' };

function statusChip(link: LinkedZoneDoc) {
    if (link.enabled) {
        return <Chip size="small" color="success" label={i18n('linked_project_status_enabled')} sx={chipSx} />;
    }
    if (link.disabledReason === 'mapping_error') {
        return <Chip size="small" color="warning" label={i18n('linked_project_status_mapping_error')} sx={chipSx} />;
    }
    return <Chip size="small" color="error" variant="outlined" label={i18n('linked_project_status_disabled')} sx={chipSx} />;
}

interface InlineLinksSectionProps {
    zoneId: string;
    linkCount: number;
    refreshKey?: number;
    onManageLinks?: () => void;
    headerText?: string;
    hideHeader?: boolean;
}

function InlineLinksSection({ zoneId, linkCount, refreshKey, onManageLinks, headerText, hideHeader }: InlineLinksSectionProps) {
    const [links, setLinks] = React.useState<LinkedZoneDoc[] | null>(null);

    React.useEffect(() => {
        lediumApi.get<LinkedZoneDoc[]>(`linkedZones?sourceZoneIds=${zoneId}`).then((data) => {
            setLinks((data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)));
        });
    }, [zoneId, linkCount, refreshKey]);

    return (
        <Box sx={{ mt: 0.5 }}>
            {!hideHeader && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, my: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {headerText ?? i18n('linked_project_inline_header', linkCount)}
                    </Typography>
                    {onManageLinks && (
                        <Button
                            size="small"
                            variant="contained"
                            sx={{ p: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onManageLinks();
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {i18n('edit')}
                        </Button>
                    )}
                </Box>
            )}
            {links === null && <Skeleton width={120} sx={{ ml: 'auto' }} />}
            {links !== null && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 1, rowGap: 1, alignItems: 'center' }}>
                    {links.map((link) => (
                        <React.Fragment key={link._id}>
                            <Typography variant="caption" noWrap sx={{ textAlign: 'right' }}>
                                {link.name}
                            </Typography>
                            <LinkTypeChip type={link.type} />
                            {statusChip(link)}
                        </React.Fragment>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default InlineLinksSection;
